import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { BiometricAuthService, AuthResult } from '../../services/biometric-auth.service';
import { AuthService } from '../../services/auth.service';

import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-biometric-login',
    imports: [FormsModule],
    templateUrl: './biometric-login.component.html',
    styleUrls: ['./biometric-login.component.scss']
})
export class BiometricLoginComponent implements OnInit {
  username: string = '';
  email: string = '';
  displayName: string = '';

  isSupported: boolean = false;
  platformAuthenticator: boolean = false;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  mode: 'login' | 'register' = 'login';
  returnUrl: string = '/app';

  // Expose for template if needed
  isDevMode = (environment as any).disableBiometricAuth;

  constructor(
    private biometricService: BiometricAuthService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  async ngOnInit() {
    // Récupérer l'URL de retour
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/app';

    // Si déjà authentifié, rediriger
    if (this.authService.isAuthenticated()) {
      this.router.navigate([this.returnUrl]);
      return;
    }

    // Vérifier le support WebAuthn
    this.isSupported = this.biometricService.isSupported();

    // En mode dev, on considère que c'est supporté même si le navigateur ne le gère pas
    if (this.isDevMode) {
      this.isSupported = true;
      this.platformAuthenticator = true;
    } else if (this.isSupported) {
      const availability = await this.biometricService.checkBiometricAvailability();
      this.platformAuthenticator = availability.platformAuthenticator;
    }
  }

  /**
   * Basculer entre connexion et enregistrement
   */
  toggleMode() {
    this.mode = this.mode === 'login' ? 'register' : 'login';
    this.errorMessage = '';
    this.successMessage = '';
  }

  /**
   * Se connecter avec l'empreinte digitale
   */
  async loginWithFingerprint() {
    console.log('🔐 Début de l\'authentification pour:', this.username);

    if (!this.username.trim()) {
      this.errorMessage = 'Veuillez entrer votre nom d\'utilisateur';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      // Vérifier si l'utilisateur existe
      let userCheck = { exists: false, hasCredentials: false };

      if (this.isDevMode) {
        console.log('🔧 Mode DEV: Bypass vérification utilisateur');
        userCheck = { exists: true, hasCredentials: true };
      } else {
        console.log('🔍 Vérification de l\'existence de l\'utilisateur...');
        userCheck = await this.biometricService.checkUserExists(this.username);
        console.log('✅ Résultat vérification:', userCheck);
      }

      if (!userCheck.exists) {
        this.errorMessage = 'Utilisateur introuvable. Veuillez vous enregistrer d\'abord.';
        this.isLoading = false;
        return;
      }

      if (!userCheck.hasCredentials) {
        this.errorMessage = 'Aucune empreinte enregistrée. Veuillez vous enregistrer d\'abord.';
        this.isLoading = false;
        return;
      }

      // Authentifier
      let result: AuthResult;

      if (this.isDevMode) {
        console.log('🔧 Mode DEV: Bypass authentification biométrique');
        await new Promise(resolve => setTimeout(resolve, 500)); // Simuler délai
        result = {
          verified: true,
          user: {
            id: 999,
            username: this.username,
            email: 'dev@example.com',
            displayName: `${this.username} (Dev)`
          }
        };
      } else {
        console.log('👆 Lancement de l\'authentification biométrique...');
        result = await this.biometricService.authenticateWithFingerprint(this.username);
      }

      console.log('📥 Résultat authentification:', result);

      if (result.verified && result.user) {
        this.successMessage = `Bienvenue ${result.user.displayName || result.user.username} !`;

        // Enregistrer l'utilisateur dans le service d'authentification
        console.log('💾 Enregistrement de l\'utilisateur dans le service auth');
        this.authService.setUser(result.user);

        // Rediriger vers la page demandée après 1 seconde
        setTimeout(() => {
          this.router.navigate([this.returnUrl]);
        }, 1000);
      } else {
        this.errorMessage = result.error || 'Authentification échouée';
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.errorMessage = 'Une erreur est survenue lors de l\'authentification';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Enregistrer une nouvelle empreinte digitale
   */
  async registerFingerprint() {
    if (!this.username.trim() || !this.email.trim()) {
      this.errorMessage = 'Veuillez remplir tous les champs obligatoires';
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.email)) {
      this.errorMessage = 'Veuillez entrer une adresse email valide';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const result: AuthResult = await this.biometricService.registerFingerprint(
        this.username,
        this.email,
        this.displayName || this.username
      );

      if (result.verified) {
        this.successMessage = 'Empreinte enregistrée avec succès ! Vous pouvez maintenant vous connecter.';

        // Basculer vers le mode connexion après 2 secondes
        setTimeout(() => {
          this.mode = 'login';
          this.errorMessage = '';
          this.successMessage = '';
        }, 2000);
      } else {
        this.errorMessage = result.error || 'Enregistrement échoué';
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.errorMessage = 'Une erreur est survenue lors de l\'enregistrement';
    } finally {
      this.isLoading = false;
    }
  }
}
